import { lazy, Suspense, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardCheck, Loader2, Search, ChevronDown, ChevronUp,
  Scale, Home, TrendingUp, MapPin, Shield, FileText, StickyNote, Pencil,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../services/api';
import { hasBasicAccess, premiumFeatureHint, premiumUpgradeHref, premiumUpgradeLabel } from '../utils/access';
import { formatCurrency } from '../utils/formatters';
import ComparablesPanel, { EVIDENCE_META } from '../components/analysis/ComparablesPanel';
import SalesHistoryChart from '../components/charts/SalesHistoryChart';
import PriceHistogramChart from '../components/charts/PriceHistogramChart';
import { buildValuationNarrative } from '../utils/valuationNarrative';

const NeighbourhoodMap = lazy(() => import('../components/maps/NeighbourhoodMap'));

const PROPERTY_TYPES = [
  { value: '', label: 'Auto (from EPC)' },
  { value: 'detached', label: 'Detached' },
  { value: 'semi-detached', label: 'Semi-detached' },
  { value: 'terraced', label: 'Terraced (mid)' },
  { value: 'end_of_terrace', label: 'End of terrace' },
  { value: 'bungalow', label: 'Bungalow' },
  { value: 'flat', label: 'Flat' },
  { value: 'maisonette', label: 'Maisonette' },
];

const TYPE_LABELS = Object.fromEntries(
  PROPERTY_TYPES.filter((t) => t.value).map((t) => [t.value, t.label]),
);

function formatPropertyType(value) {
  if (!value) return '—';
  return TYPE_LABELS[value] || TYPE_LABELS[value.replace(/_/g, '-')] || value.replace(/_/g, ' ');
}

const ROLE_PRESETS = [
  { id: 'all', label: 'All sections' },
  { id: 'valuer', label: 'Valuer / Lender' },
  { id: 'agent', label: 'Estate agent' },
  { id: 'investor', label: 'Investor' },
];

const SECTION_META = {
  subject: { label: 'Subject property', icon: Home, roles: ['all', 'valuer', 'agent', 'investor'] },
  valuation: { label: 'Indicative value & comparables', icon: Scale, roles: ['all', 'valuer', 'agent', 'investor'] },
  methodology: { label: 'How we calculated this', icon: FileText, roles: ['all', 'valuer', 'agent'] },
  trends: { label: 'Area price trends', icon: TrendingUp, roles: ['all', 'valuer', 'agent', 'investor'] },
  distribution: { label: 'Price distribution', icon: TrendingUp, roles: ['all', 'valuer', 'agent', 'investor'] },
  neighbourhood: { label: 'Neighbourhood & risk overlays', icon: MapPin, roles: ['all', 'valuer', 'agent', 'investor'] },
  notes: { label: 'Professional notes', icon: StickyNote, roles: ['all', 'valuer', 'agent'] },
};

const DEFAULT_EXPANDED = {
  all: Object.keys(SECTION_META),
  valuer: ['subject', 'valuation', 'methodology', 'trends', 'neighbourhood', 'notes'],
  agent: ['subject', 'valuation', 'trends', 'distribution', 'neighbourhood', 'notes'],
  investor: ['subject', 'valuation', 'trends', 'distribution'],
};

export default function PropertyAnalysis() {
  const { user } = useAuth();
  const canRun = hasBasicAccess(user);
  const [address, setAddress] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [role, setRole] = useState('valuer');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set(DEFAULT_EXPANDED.valuer));
  const [notes, setNotes] = useState('');
  const [professionalValue, setProfessionalValue] = useState('');
  const [correcting, setCorrecting] = useState(false);
  const [editType, setEditType] = useState('');
  const [editBeds, setEditBeds] = useState('');

  const visibleSections = useMemo(
    () => Object.entries(SECTION_META).filter(([, m]) => m.roles.includes(role)),
    [role],
  );

  const applyRole = (nextRole) => {
    setRole(nextRole);
    setExpanded(new Set(DEFAULT_EXPANDED[nextRole] || DEFAULT_EXPANDED.all));
  };

  const toggleSection = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runAnalysis = async (e, overrides = null) => {
    e?.preventDefault();
    if (!canRun || !address.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const typeVal = overrides?.property_type ?? propertyType;
      const bedsVal = overrides?.bedrooms ?? bedrooms;
      const body = { address: address.trim() };
      if (typeVal) body.property_type = typeVal;
      if (bedsVal !== '' && bedsVal != null) body.bedrooms = parseInt(bedsVal, 10);
      const res = await authFetch('/api/analysis/address', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        timeoutMs: 60000,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Analysis failed');
      setReport(data);
      if (overrides) {
        setPropertyType(overrides.property_type || '');
        setBedrooms(overrides.bedrooms === '' || overrides.bedrooms == null ? '' : String(overrides.bedrooms));
      }
      setCorrecting(false);
    } catch (err) {
      setError(err.message);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const openCorrect = () => {
    const src = report?.attributes_source || {};
    const currentType = report?.property_type || '';
    const known = PROPERTY_TYPES.some((t) => t.value === currentType);
    setEditType(known ? currentType : (src.epc_mapped_type && PROPERTY_TYPES.some((t) => t.value === src.epc_mapped_type) ? src.epc_mapped_type : ''));
    setEditBeds(report?.bedrooms != null ? String(report.bedrooms) : '');
    setCorrecting(true);
  };

  const applyCorrection = (e) => {
    e?.preventDefault();
    runAnalysis(null, {
      property_type: editType,
      bedrooms: editBeds,
    });
  };

  const typeCorrected = report?.attributes_source?.property_type === 'user';
  const bedsCorrected = report?.attributes_source?.bedrooms === 'user';

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-violet-300">
          <ClipboardCheck size={22} />
          <span className="text-sm font-semibold uppercase tracking-wider">Professional desk report</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-white">Property analysis</h1>
        <p className="text-slate-400 max-w-3xl">
          Enter any UK address for indicative value, comparable sales, area trends, neighbourhood risk,
          and a transparent methodology — for valuers, agents, and investors. Subject to site inspection
          and professional judgement; not a RICS Red Book valuation.
        </p>
      </header>

      <form onSubmit={runAnalysis} className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Full address including postcode</label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="e.g. 152 Briar Road, Watford WD25 0HH"
            className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-4 items-end">
          <div className="min-w-[11rem]">
            <label className="block text-xs text-slate-500 mb-1">Property type</label>
            <select
              value={propertyType}
              onChange={(e) => setPropertyType(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm"
            >
              {PROPERTY_TYPES.map((t) => (
                <option key={t.value || 'auto'} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="w-32">
            <label className="block text-xs text-slate-500 mb-1">Bedrooms</label>
            <input
              type="number"
              min="0"
              max="20"
              value={bedrooms}
              onChange={(e) => setBedrooms(e.target.value)}
              placeholder="Auto"
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={!canRun || loading || !address.trim()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            Run analysis
          </button>
        </div>
        <p className="text-xs text-slate-500">
          Leave type/beds on Auto to use EPC records. Correct them if the record is wrong — e.g. end-of-terrace vs semi-detached.
        </p>
        {!canRun && (
          <p className="text-xs text-slate-500">
            {premiumFeatureHint(user)}{' '}
            <Link to={premiumUpgradeHref(user)} className="text-emerald-400 hover:underline">
              {premiumUpgradeLabel(user)}
            </Link>
            {' · '}Try promo code <span className="font-mono text-slate-400">VALUER-21D</span> on your account page (21-day trial).
          </p>
        )}
      </form>

      <div className="flex flex-wrap gap-2">
        {ROLE_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => applyRole(p.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              role === p.id
                ? 'bg-violet-500/15 border-violet-500/40 text-violet-200'
                : 'border-slate-800 text-slate-500 hover:text-slate-300'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-300 text-sm">{error}</div>
      )}

      {loading && (
        <div className="flex items-center justify-center gap-3 py-16 text-slate-400">
          <Loader2 className="animate-spin text-violet-400" />
          Building desk report…
        </div>
      )}

      {report && !loading && (
        <div className="space-y-4">
          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl px-5 py-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-white font-semibold text-lg">{report.address}</p>
                <p className="text-slate-500 text-sm mt-1">
                  {formatPropertyType(report.property_type)}
                  {report.bedrooms != null ? ` · ${report.bedrooms} bed` : ''}
                  {(typeCorrected || bedsCorrected) && (
                    <span className="ml-2 text-amber-400/90 text-xs font-medium">corrected</span>
                  )}
                  {report.listed && report.property_id && (
                    <>
                      {' · '}
                      <Link to={`/properties/${report.property_id}`} className="text-emerald-400 hover:underline">
                        View listing
                      </Link>
                    </>
                  )}
                </p>
                {report.attributes_source?.epc_built_form && report.attributes_source?.property_type !== 'user' && (
                  <p className="text-xs text-slate-600 mt-1">
                    EPC built form: {report.attributes_source.epc_built_form}
                    {report.attributes_source.epc_mapped_type
                      ? ` → ${formatPropertyType(report.attributes_source.epc_mapped_type)}`
                      : ''}
                  </p>
                )}
              </div>
              <div className="flex items-start gap-3">
                {report.valuation?.estimate_mid && (
                  <div className="text-right">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Indicative mid</p>
                    <p className="text-2xl font-bold text-emerald-400">
                      {formatCurrency(report.valuation.estimate_mid)}
                    </p>
                    {report.valuation?.comparable_count > 0 && (
                      <p className="text-xs text-slate-500 mt-1 max-w-[14rem]">
                        From {report.valuation.comparable_count} similar sale
                        {report.valuation.comparable_count === 1 ? '' : 's'}
                        {report.area_stats?.growth_pct_5yr != null && (
                          <> · area {report.area_stats.growth_pct_5yr >= 0 ? '+' : ''}
                          {(report.area_stats.growth_pct_5yr * 100).toFixed(1)}% / 5yr</>
                        )}
                      </p>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={openCorrect}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 text-xs font-semibold"
                >
                  <Pencil size={14} />
                  Correct details
                </button>
              </div>
            </div>

            {correcting && (
              <form
                onSubmit={applyCorrection}
                className="border-t border-slate-800 pt-3 flex flex-wrap gap-3 items-end"
              >
                <div className="min-w-[11rem]">
                  <label className="block text-xs text-slate-500 mb-1">Correct property type</label>
                  <select
                    value={editType}
                    onChange={(e) => setEditType(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm"
                  >
                    {PROPERTY_TYPES.map((t) => (
                      <option key={t.value || 'auto'} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="w-32">
                  <label className="block text-xs text-slate-500 mb-1">Correct bedrooms</label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    value={editBeds}
                    onChange={(e) => setEditBeds(e.target.value)}
                    placeholder="Auto"
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold disabled:opacity-50"
                >
                  Update valuation
                </button>
                <button
                  type="button"
                  onClick={() => setCorrecting(false)}
                  className="px-3 py-2.5 text-xs text-slate-500 hover:text-slate-300"
                >
                  Cancel
                </button>
                <p className="w-full text-xs text-slate-500">
                  Re-runs the comparable search with your corrected attributes. EPC records are unchanged.
                </p>
              </form>
            )}
          </div>

          {visibleSections.map(([id, meta]) => (
            <AnalysisSection
              key={id}
              id={id}
              meta={meta}
              open={expanded.has(id)}
              onToggle={() => toggleSection(id)}
            >
              {id === 'subject' && <SubjectSection report={report} />}
              {id === 'valuation' && (
                <ComparablesPanel
                  avm={report.valuation}
                  loading={false}
                  postcode={report.postcode}
                  areaStats={report.area_stats}
                />
              )}
              {id === 'methodology' && (
                <MethodologySection
                  methodology={report.methodology}
                  avm={report.valuation}
                  areaStats={report.area_stats}
                  postcode={report.postcode}
                />
              )}
              {id === 'trends' && (
                <SalesHistoryChart
                  data={report.area_trends?.sales_by_year}
                  currentPrice={report.valuation?.estimate_mid}
                />
              )}
              {id === 'distribution' && (
                <PriceHistogramChart
                  data={report.price_distribution}
                  guidePrice={report.valuation?.estimate_mid}
                  district={report.area_trends?.district}
                />
              )}
              {id === 'neighbourhood' && <NeighbourhoodSection report={report} />}
              {id === 'notes' && (
                <NotesSection
                  notes={notes}
                  setNotes={setNotes}
                  professionalValue={professionalValue}
                  setProfessionalValue={setProfessionalValue}
                  role={role}
                />
              )}
            </AnalysisSection>
          ))}

          <p className="text-xs text-slate-600 text-center pt-4">
            {report.methodology?.disclaimer}
          </p>
        </div>
      )}
    </div>
  );
}

function AnalysisSection({ id, meta, open, onToggle, children }) {
  const Icon = meta.icon;
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-800/40 transition-colors"
      >
        <span className="flex items-center gap-2 text-white font-semibold text-sm">
          <Icon size={16} className="text-violet-400" />
          {meta.label}
        </span>
        {open ? <ChevronUp size={18} className="text-slate-500" /> : <ChevronDown size={18} className="text-slate-500" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-slate-800 pt-4">{children}</div>}
    </section>
  );
}

function SubjectSection({ report }) {
  const epc = report.epc || {};
  const src = report.attributes_source || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Property type" value={formatPropertyType(report.property_type)} />
        <Stat label="Bedrooms" value={report.bedrooms != null ? report.bedrooms : '—'} />
        <Stat label="Postcode" value={report.postcode} />
        <Stat label="EPC rating" value={epc.energy_rating || epc.current_energy_rating || '—'} />
        <Stat label="Floor area" value={epc.floor_area_sqm || epc.total_floor_area ? `${epc.floor_area_sqm || epc.total_floor_area} m²` : '—'} />
        <Stat label="EPC built form" value={epc.built_form || '—'} />
        {report.area_stats?.avg_price_5yr && (
          <Stat label="District avg (5yr)" value={formatCurrency(report.area_stats.avg_price_5yr)} />
        )}
        {report.area_stats?.growth_pct_5yr != null && (
          <Stat label="5yr growth" value={`${(report.area_stats.growth_pct_5yr * 100).toFixed(1)}%`} />
        )}
      </div>
      {(src.property_type === 'user' || src.bedrooms === 'user') && (
        <p className="text-xs text-amber-400/90">
          Valuation uses your corrected attributes
          {src.epc_mapped_type ? ` (EPC suggested ${formatPropertyType(src.epc_mapped_type)})` : ''}.
        </p>
      )}
    </div>
  );
}

function MethodologySection({ methodology, avm, areaStats, postcode }) {
  if (!methodology) return null;
  const evidence = EVIDENCE_META[avm?.evidence_tier] || EVIDENCE_META.none;
  const story = buildValuationNarrative({ avm, areaStats, postcode });
  return (
    <div className="space-y-4">
      {story && (
        <div className="rounded-xl border border-violet-500/25 bg-violet-500/5 px-4 py-3 space-y-2">
          <p className="text-sm font-semibold text-violet-200">{story.title}</p>
          {story.steps.map((step) => (
            <p key={step.label} className="text-sm text-slate-300">
              <span className="text-slate-500 font-medium">{step.label}: </span>
              {step.text}
            </p>
          ))}
        </div>
      )}
      <div className={`inline-flex text-xs font-semibold px-3 py-1 rounded-full border ${evidence.colour}`}>
        {methodology.evidence_summary || evidence.label}
      </div>
      <ol className="space-y-3">
        {(methodology.steps || []).map((step) => (
          <li key={step.step} className="flex gap-3">
            <span className="shrink-0 w-6 h-6 rounded-full bg-violet-500/20 text-violet-300 text-xs font-bold flex items-center justify-center">
              {step.step}
            </span>
            <div>
              <p className="text-sm text-white font-medium">{step.title}</p>
              <p className="text-sm text-slate-400 mt-0.5">{step.detail}</p>
            </div>
          </li>
        ))}
      </ol>
      <p className="text-xs text-slate-500 border-t border-slate-800 pt-3">
        Confidence: <span className="text-slate-300">{avm?.confidence || '—'}</span>
        {' · '}Comparable count: <span className="text-slate-300">{avm?.comparable_count ?? 0}</span>
      </p>
    </div>
  );
}

function NeighbourhoodSection({ report }) {
  const nb = report.neighbourhood;
  if (!nb) {
    return (
      <p className="text-slate-500 text-sm">
        Neighbourhood data unavailable for this postcode.{' '}
        <Link to={`/neighbourhood/${encodeURIComponent(report.postcode)}`} className="text-blue-400 hover:underline">
          Try area lookup
        </Link>
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {nb.imd_band && <Stat label="IMD band" value={nb.imd_band} />}
        {nb.crime?.rate_band && <Stat label="Crime rate" value={nb.crime.rate_band} />}
        {nb.broadband_sfbb_pct != null && <Stat label="Superfast broadband" value={`${nb.broadband_sfbb_pct}%`} />}
        {nb.flood_risk && <Stat label="Flood risk" value={nb.flood_risk} />}
      </div>
      {nb.latitude && nb.longitude && (
        <Suspense fallback={<div className="h-64 bg-slate-800 rounded-xl animate-pulse" />}>
          <div className="h-72 rounded-xl overflow-hidden border border-slate-800">
            <NeighbourhoodMap
              center={{ lat: nb.latitude, lng: nb.longitude }}
              schools={nb.schools || []}
              transport={nb.transport || []}
              planning={nb.planning || []}
              crimePoints={nb.crime_heatmap || []}
              heatmapGated={!!nb.gated}
              nearbyProperties={nb.nearby_for_sale || []}
              height="288px"
              showLegend={false}
            />
          </div>
        </Suspense>
      )}
      <Link
        to={`/neighbourhood/${encodeURIComponent(report.postcode)}`}
        className="text-sm text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
      >
        <Shield size={14} /> Full neighbourhood report
      </Link>
    </div>
  );
}

function NotesSection({ notes, setNotes, professionalValue, setProfessionalValue, role }) {
  const label = role === 'valuer'
    ? 'Lending / security value (after inspection)'
    : role === 'agent'
      ? 'Recommended asking range (after viewing)'
      : 'Professional assessment';

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">
        Private to your browser session — for your file after site visit or vendor meeting.
      </p>
      <div>
        <label className="block text-xs text-slate-500 mb-1">{label}</label>
        <input
          type="text"
          value={professionalValue}
          onChange={(e) => setProfessionalValue(e.target.value)}
          placeholder="e.g. £285,000 – £295,000"
          className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-1">Notes</label>
        <textarea
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Condition observations, adjustments, lending policy, marketing strategy…"
          className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm resize-y"
        />
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-800/50 rounded-lg p-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-sm text-white font-medium mt-1">{value}</p>
    </div>
  );
}
